import { Nub } from "../../core";
import { type $ } from "../../dom";
import { createEventNotifier } from "../../event-notifier";

export type InputOptions<TValue> = {
  resetValue: TValue;

  $findInput?: ($root: $) => $;

  isRequired?: boolean;
};

export abstract class Input<TValue = unknown, TData = TValue> extends Nub {
  private readonly inputEventNotifier = createEventNotifier<() => void>();
  private readonly changeEventNotifier = createEventNotifier<() => void>();

  public resetValue: TValue;

  public isRequired: boolean;

  public readonly $input: $;

  constructor($root: $, options: InputOptions<TValue>) {
    super($root, {
      type: "single",
      name: "input",
    });

    const { resetValue, $findInput, isRequired = false } = options;

    this.$input =
      $findInput?.($root).transform(($) => this.$root.make($)) ??
      this.$root.make($root);

    this.resetValue = resetValue;
    this.isRequired = isRequired;
  }

  abstract get value(): TValue;
  abstract set value(value: TValue);

  abstract get data(): TData;

  abstract get isActive(): boolean;

  get isValid(): boolean {
    return this.isRequired ? this.isActive : true;
  }

  reset(): void {
    this.value = this.resetValue;
  }

  onInput(callback: () => void): void {
    this.inputEventNotifier.addListener(callback);
  }

  onChange(callback: () => void): void {
    this.changeEventNotifier.addListener(callback);
  }

  triggerInput(): void {
    this.inputEventNotifier.notifyListeners();
  }

  triggerChange(): void {
    this.changeEventNotifier.notifyListeners();
  }

  sleepOnInput(): void {
    this.inputEventNotifier.sleep();
  }

  wakeOnInput(): void {
    this.inputEventNotifier.wake();
  }

  sleepOnChange(): void {
    this.changeEventNotifier.sleep();
  }

  wakeOnChange(): void {
    this.changeEventNotifier.wake();
  }

  forwardEventsTo(other: Input): void {
    this.onInput(() => other.triggerInput());
    this.onChange(() => other.triggerChange());
  }

  destroy(): void {
    this.$input.off(":relkit-input");
  }
}
